/**
 * Titles page — displays a paginated, filterable table of all titles.
 * Filter state (country, locale, publisher, campaigns, search) is persisted
 * in URL search params so links can be shared/bookmarked. Pagination uses
 * page-based offset and is NOT persisted in the URL.
 */
import React, { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import { useTranslation } from 'src/arb-wrapper';
import { useGetDistinctTitlesQuery, useCampaignTitlesControllerGetCampaignTitlesQuery } from 'src/store/leela-api';
import { type CampaignTitlesControllerGetCampaignTitlesApiArg, type CampaignTitle } from 'src/store/generated/leela-api-generated';
import { type TitlesFilterState } from 'src/types/titles.types';
import { epochToIsoDate } from 'src/utils/releaseDate';
import { TitlesActionBar } from 'src/components/titles/TitlesActionBar';
import { TitlesTable, type TitleInfo } from 'src/components/titles/TitlesTable';

export const defaultFilters: TitlesFilterState = {
  country: '',
  locale: '',
  publisher: '',
  campaignIds: [],
};

/** Derive TitlesFilterState from URL search params. */
function filtersFromParams (params: URLSearchParams): TitlesFilterState {
  const campaignsRaw = params.get('campaigns') ?? '';
  return {
    country: params.get('country') ?? '',
    locale: params.get('locale') ?? '',
    publisher: params.get('publisher') ?? '',
    campaignIds: campaignsRaw ? campaignsRaw.split(',').filter(Boolean) : [],
  };
}

/** Derive search string from URL search params. */
function searchFromParams (params: URLSearchParams): string {
  return params.get('search') ?? '';
}

/** Write filter state + search to URL params, omitting empty/default values for clean URLs. */
function buildSearchParams (filters: TitlesFilterState, search: string): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.country) params.set('country', filters.country);
  if (filters.locale) params.set('locale', filters.locale);
  if (filters.publisher) params.set('publisher', filters.publisher);
  if (filters.campaignIds.length > 0) params.set('campaigns', filters.campaignIds.join(','));
  if (search) params.set('search', search);
  return params;
}

export const TitlesTab: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Source of truth for filters and search is the URL
  const filters = useMemo(() => filtersFromParams(searchParams), [searchParams]);
  const search = useMemo(() => searchFromParams(searchParams), [searchParams]);

  const [pagination, setPagination] = useState({ page: 1, pageSize: 10 });

  // Build query args for the distinct titles endpoint
  const queryArg = useMemo(() => ({
    page: pagination.page,
    pageSize: pagination.pageSize,
    country: filters.country || undefined,
    locale: filters.locale || undefined,
    search: search ? Array.from(new TextEncoder().encode(search), (b) => b.toString(16).padStart(2, '0')).join('') : undefined,
    sort: ['name:1'],
    publisher: filters.publisher || undefined,
    campaignIds: filters.campaignIds.length > 0 ? filters.campaignIds : undefined,
  }), [filters, search, pagination]);

  const { data, isLoading, isFetching, error } = useGetDistinctTitlesQuery(queryArg);

  // Client-side enrichment: fetch title metadata for the current page's GTIs
  const gtisForEnrichment = useMemo(
    () => (data?.data ?? []).map((item) => item.gti).filter(Boolean),
    [data],
  );

  const campaignTitlesQueryArgs: CampaignTitlesControllerGetCampaignTitlesApiArg | undefined = useMemo(() => {
    if (gtisForEnrichment.length === 0) return undefined;
    return {
      gtiList: gtisForEnrichment,
      countryCode: (filters.country || 'US') as CampaignTitlesControllerGetCampaignTitlesApiArg['countryCode'],
      locale: (filters.locale || 'en_US') as CampaignTitlesControllerGetCampaignTitlesApiArg['locale'],
    };
  }, [gtisForEnrichment, filters.country, filters.locale]);

  const { data: campaignTitles, isLoading: isLoadingEnrichment } = useCampaignTitlesControllerGetCampaignTitlesQuery(
    campaignTitlesQueryArgs!,
    { skip: campaignTitlesQueryArgs === undefined },
  );

  // Merge aggregation data with enriched metadata into TitleInfo[]
  const titles: TitleInfo[] = useMemo(() => {
    const baseTitles = data?.data ?? [];
    const metadataMap = new Map<string, CampaignTitle>(
      (campaignTitles ?? []).map((ct) => [ct.gti, ct]),
    );

    return baseTitles.map((title) => {
      const meta = metadataMap.get(title.gti);
      return {
        gti: title.gti,
        name: meta?.title ?? title.name,
        description: meta?.description ?? undefined,
        genres: meta?.genres ?? undefined,
        coverImageUrl: meta?.coverImageUrl ?? undefined,
        campaignNames: title.campaignNames?.length ? title.campaignNames : [],
        campaignIds: title.campaignIds ?? [],
        releaseDate: epochToIsoDate(meta?.publicReleaseDate),
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- intentional: need to fall through on length === 0
        contributors: (meta?.directors?.length || meta?.starringCast?.length)
          ? { directors: meta?.directors ?? [], actors: meta?.starringCast ?? [], producers: [] }
          : undefined,
      };
    });
  }, [data, campaignTitles]);

  // Reset pagination to page 1 (shared by filter and search handlers)
  const resetPagination = useCallback(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  const handleFiltersChange = useCallback((newFilters: TitlesFilterState) => {
    setSearchParams(() => buildSearchParams(newFilters, search), { replace: true });
    resetPagination();
  }, [setSearchParams, search, resetPagination]);

  const handleSearchChange = useCallback((newSearch: string) => {
    setSearchParams(() => buildSearchParams(filters, newSearch), { replace: true });
    resetPagination();
  }, [setSearchParams, filters, resetPagination]);

  const handleClearAll = useCallback(() => {
    setSearchParams(new URLSearchParams(), { replace: true });
    resetPagination();
  }, [resetPagination, setSearchParams]);

  const paginationMeta = data?.meta?.pagination;

  return (
    <Column alignmentHorizontal="stretch" heights="fill" spacing="400">
      <Heading level={1} type="h500">
        {t('titles')}
      </Heading>

      <TitlesActionBar
        filters={filters}
        search={search}
        onClearAll={handleClearAll}
        onFiltersChange={handleFiltersChange}
        onSearchChange={handleSearchChange}
      />

      <TitlesTable
        cursorPagination={{
          page: pagination.page,
          pageSize: paginationMeta?.pageSize ?? pagination.pageSize,
          hasMore: data?.hasMore ?? false,
          isLoading: isFetching && !isLoading,
          onNextPage: () => {
            if (data?.hasMore) {
              setPagination((prev) => ({ ...prev, page: prev.page + 1 }));
            }
          },
          onPreviousPage: () => {
            setPagination((prev) => ({ ...prev, page: Math.max(1, prev.page - 1) }));
          },
          onPageSizeChange: (pageSize) => {
            setPagination({ page: 1, pageSize });
          },
        }}
        hasError={!!error}
        isEnriching={isLoadingEnrichment}
        isFetching={isFetching && !isLoading}
        isLoading={isLoading}
        titles={titles}
      />
    </Column>
  );
};
